import React, { useRef, useEffect, useState, useCallback } from "react";
import WorkCard from "./WorkCard";
import "./FeaturedWork.scss";

function loadImage(filename, fallbackLabel) {
  const placeholder =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='800'>
         <rect width='600' height='800' fill='%23D9D5CF'/>
         <text x='50%' y='50%' font-family='sans-serif' font-size='22'
               fill='%23767066' text-anchor='middle'>${fallbackLabel}</text>
       </svg>`
    );

  try {
    const ctx = require.context(
      "../../../../resources/images/brandkeeper",
      false,
      /\.(png|jpe?g|webp)$/
    );
    try { return ctx(`./${filename}`); } catch (_) {}
    return placeholder;
  } catch {
    return placeholder;
  }
}

const PROJECTS = {
  p08: {
    id: "p8",
    seq: 8,
    size: "short",
    image: loadImage("industrial-services-social-media-ad-design-2.jpg", "Bilingual Social Ads"),
    alt: "Four social media ad graphics for PM Generator Service LLC in both English and Spanish, showing a yellow industrial generator with a hurricane prep checklist, diesel fuel contamination comparison jars from a boat engine, a technician's gloved hand holding a sample jar with Spanish text \"Protege tu equipo Diesel,\" and a docked boat with the tagline \"Everyday sunny? well nope.\"",
    category: "Field Services",
    deliverable: "Bilingual Social Ad Design",
    title: "Bilingual Social Ads for a Marine and Generator Diesel Service",
    location: "Puerto Rico, USA",
    description: "This client serves two audiences at once, generator owners bracing for hurricane season and boat owners fighting diesel fuel contamination, and needed ad creative that spoke to both, in English and Spanish. We built a bilingual ad set using real before-and-after fuel sample photography to make an invisible problem visible, giving the sales team creative that did the explaining for them before a single phone call happened.",
    hideAddon: true,
  },
  p09: {
    id: "p9",
    seq: 9,
    size: "short",
    image: loadImage("family-fashion-shopify-campaign-banner.jpg", "Fashion Campaign Banner"),
    alt: "Shopify campaign banner designs for the Zoraida Collazo fashion label, including a \"Holiday Styles\" banner featuring a model in a red dress, a beach lifestyle photo with flowing fabric, and an \"Impress Yourself\" polka dot dress campaign banner shot outdoors.",
    category: "Fashion",
    deliverable: "Shopify Campaign Banners",
    title: "Shopify Campaign Banners for a Women's Fashion Label",
    location: "Puerto Rico, USA",
    description: "A seasonal collection only sells if the storefront makes people stop scrolling. We built a set of campaign banners for this fashion label mixing real lifestyle photography with clean typography, giving every collection launch, from holiday dresses to a beachside campaign, its own polished moment on the homepage.",
  },
  p10: {
    id: "p10",
    seq: 10,
    size: "tall",
    image: loadImage("lifestyle-brand-custom-logo-design-and-brand-identity.jpg", "Apparel Merch Design"),
    alt: "Black and white t-shirt merch design for the \"Diesel Junkie PR\" apparel brand, showing a mechanic skull mascot logo printed on shirt backs in both black and white colorways, alongside a Thanksgiving and Black Friday sale promotion graphic listing sizes and pricing.",
    category: "Apparel",
    deliverable: "Merch Design & Promo Creative",
    title: "Streetwear Merch Design for a Diesel Culture Apparel Brand",
    location: "Puerto Rico, USA",
    description: "This client needed merch that felt authentic to truck and diesel culture, not generic. We built a skull-and-wrench mascot mark strong enough to hold up on a t-shirt back print, then turned it around into a Black Friday promo ad that gave the sale a real sense of urgency without losing the brand's edge.",
  },
  p01: {
    id: "p1",
    seq: 1,
    size: "tall",
    image: loadImage("retail-custom-logo-design-and-brand-identity.jpg", "Beverage Branding"),
    alt: "Premium seltzer can design for the \"Loco\" beverage brand, featuring a hand-illustrated cartoon woman mascot, pink script logo lettering, \"Low Calorie · Low Carb\" badge, and \"Premium Seltzer\" text, photographed with a citrus splash background.",
    category: "Beverage",
    deliverable: "Can Label & Brand Identity",
    title: "Can Design and Brand Identity for a Craft Seltzer Label",
    location: "London, UK",
    description: "A seltzer can has about half a second to earn a glance on a crowded shelf. We built a full character-driven identity around \"Loco,\" giving the brand a hand-illustrated mascot, a playful hand-lettered wordmark, and packaging built to feel premium and fun at the same time, not just another can in the cooler.",
  },
  p02: {
    id: "p2",
    seq: 2,
    size: "wide",
    image: loadImage("industrial-services-car-magnet-sticker.jpg", "Vehicle Branding"),
    alt: "Real photo of a black, yellow, and white car door magnet sticker on a white Toyota Tacoma pickup truck, reading \"DTS Generator Preventive Maintenance and More, Sit back relax, this messy task is on us\" with a phone number, parked on a residential street in Puerto Rico.",
    category: "Field Services",
    deliverable: "Vehicle Magnet Branding",
    title: "Vehicle Magnet Branding for a Diesel Generator Specialist",
    location: "Puerto Rico, USA",
    description: "A service truck sitting in traffic is free advertising, if it actually looks like one. We designed a car magnet built to be legible at a glance from another lane, then photographed it in the real world, on the client's actual work truck, parked on an actual Puerto Rico street, so what you see is exactly what shows up on the job.",
  },
  p03: {
    id: "p3",
    seq: 3,
    size: "short",
    image: loadImage("industrial-services-flyer-design.jpg", "Seasonal Promo Flyer"),
    alt: "Two print flyers for Diesel Tech Service LLC, the first promoting a \"Summer Ready Generator\" maintenance special with 10% off and a five-point service checklist, the second explaining the \"Dieselalysis\" fuel polishing process with side-by-side clean and contaminated diesel fuel jar photography and three listed benefits.",
    category: "Field Services",
    deliverable: "Seasonal Promo Flyer",
    title: "Seasonal Promo Flyers for a Diesel Generator Specialist",
    location: "Puerto Rico, USA",
    description: "Selling a maintenance discount is one thing. Explaining a proprietary fuel treatment process most customers have never heard of is another. We designed two flyers side by side, one built around a clear seasonal promotion with a torn-paper, high-energy look, the other breaking down the client's own branded \"Dieselalysis\" fuel polishing process step by step using real before-and-after fuel samples, so the value was obvious at a glance.",
  },
  p04: {
    id: "p4",
    seq: 4,
    size: "short",
    image: loadImage("industrial-services-editable-forms.jpg", "Business Forms"),
    alt: "Two branded business forms for PM Generator Service LLC, a service invoice with generator and tank specification fields, and a detailed generator maintenance profile checklist covering inspection points, load test results, and replacement parts tracking, both in a clean dark gray and white layout.",
    category: "Field Services",
    deliverable: "Editable Business Forms",
    title: "Editable Service Forms for a Generator Maintenance Company",
    location: "Puerto Rico, USA",
    description: "Paperwork is usually the last thing a service business thinks to brand, and it shows. We rebuilt this client's invoice and inspection forms from the ground up, clean two-tone layouts, logical field grouping, and consistent branding throughout, so every document handed to a customer looked as professional as the actual work being done.",
  },
  p05: {
    id: "p5",
    seq: 5,
    size: "short",
    image: loadImage("industrial-services-social-media-ad-design-1.jpg", "Hurricane-Prep Social Ads"),
    alt: "Three social media ad graphics for Diesel Tech Service LLC promoting generator preventive maintenance, featuring a technician draining engine oil, an industrial engine close-up with the tagline \"Stable Power Support for Professionals,\" and a hurricane-damage themed ad reading \"We Can't Skip Hurricane Season But We Can Plan Ahead,\" all in the brand's black and yellow color scheme.",
    category: "Field Services",
    deliverable: "Social Ad Design",
    title: "Hurricane-Prep Social Ads for a Diesel Generator Specialist",
    location: "Puerto Rico, USA",
    description: "Hurricane season in Puerto Rico turns a broken generator into an emergency, not an inconvenience. We built a set of social ads for this diesel and generator specialist that spoke directly to that urgency, pairing bold, no-nonsense copy with real shop imagery so the brand read as capable, not corporate. The result: ad creative the team could run right through storm season, built to convert the exact moment someone starts worrying about backup power.",
    hideAddon: true,
  },
  p06: {
    id: "p6",
    seq: 6,
    size: "short",
    image: loadImage("industrial-services-work-uniforms.jpg", "Work Uniforms"),
    alt: "Black branded work t-shirt for Diesel Tech Service LLC with a front chest logo and a large back print reading \"Generator Preventive Maintenance and More,\" shown flat and worn by a technician servicing a yellow diesel generator engine on site.",
    category: "Field Services",
    deliverable: "Branded Work Uniforms",
    title: "Branded Work Uniforms for a Diesel Generator Specialist",
    location: "Puerto Rico, USA",
    description: "Every job site visit is a brand impression, whether the client thinks about it that way or not. We designed a uniform system with the primary logo on the front and a bold, readable back print for when a technician is bent over an engine bay with their back to the customer, small details, but the kind that make a two-person operation look like an established company.",
  },
  p07: {
    id: "p7",
    seq: 7,
    size: "wide",
    image: loadImage("industrial-services-service-stickers.jpg", "Service Stickers"),
    alt: "Printed sheet of branded service record stickers for Diesel Tech Service LLC, showing repeating black and white label designs with fields for date of service, next service due, hours, and a maintenance checklist, alongside the company logo and contact information.",
    category: "Field Services",
    deliverable: "Equipment Service Stickers",
    title: "Equipment Service Record Stickers for a Diesel Generator Specialist",
    location: "Puerto Rico, USA",
    description: "Maintenance stickers are usually an afterthought, plain white labels nobody remembers. We designed one that does double duty, a real service record with checkboxes and due dates the customer actually uses, printed with the brand's logo and contact info sitting right there every time someone opens the generator housing.",
  }
};

export default function FeaturedWork() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const card01Ref = useRef(null);
  const rafRef = useRef(null);
  
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const dragMovedRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const currentScrollFloatRef = useRef(0);

  const [activeProject, setActiveProject] = useState(null);
  const [isPaused, setIsPaused] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  const [revealedSeq, setRevealedSeq] = useState({});
  const [landingDone, setLandingDone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mq.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, []);

  // Alignment on mount
  const alignToCard01 = useCallback(() => {
    if (trackRef.current && card01Ref.current) {
      const cardOffsetLeft = card01Ref.current.offsetLeft;
      const initialPos = cardOffsetLeft - 40;
      trackRef.current.scrollLeft = initialPos; 
      currentScrollFloatRef.current = initialPos;
    }
  }, []);

  useEffect(() => {
    alignToCard01();
    window.addEventListener("resize", alignToCard01);
    return () => window.removeEventListener("resize", alignToCard01);
  }, [alignToCard01]);

  // Landing sequence observer
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let landingTriggered = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !landingTriggered) {
          landingTriggered = true;
          alignToCard01();

          const sequence = [1, 2, 3, 4, 5, 6, 7];
          sequence.forEach((seqNumber, index) => {
            setTimeout(() => {
              setRevealedSeq((prev) => ({ ...prev, [seqNumber]: true }));
              
              if (seqNumber === 7) {
                setTimeout(() => {
                  setLandingDone(true);
                  setIsPaused(false);
                }, 400);
              }
            }, index * 180);
          });
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [alignToCard01]);

  // Flicker-Free Continuous Auto Scroll
  useEffect(() => {
    const track = trackRef.current;
    if (!track || prefersReducedMotion || !landingDone) return;

    const autoSpeed = 0.2;

    const loop = () => {
      if (!isPaused && !isDraggingRef.current) {
        // Calculate set size based on 1 third of the 3 rendered sets
        const singleSetWidth = track.scrollWidth / 3;

        if (singleSetWidth > 0) {
          currentScrollFloatRef.current += autoSpeed;

          // Keep scrolling safely buffered inside the middle set range
          if (currentScrollFloatRef.current >= singleSetWidth * 2) {
            currentScrollFloatRef.current -= singleSetWidth;
          }

          track.scrollLeft = currentScrollFloatRef.current;
        }
      } else {
        currentScrollFloatRef.current = track.scrollLeft;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPaused, prefersReducedMotion, landingDone]);

  // Zero-Flicker Pointer Drag Logic with Triple-Buffer Wrap (mouse)
  const onPointerDown = useCallback((e) => {
    const track = trackRef.current;
    if (!track) return;
    
    isDraggingRef.current = true;
    dragMovedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = track.scrollLeft;
    
    setIsPaused(true);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const track = trackRef.current;
    if (!track) return;

    const delta = e.clientX - dragStartXRef.current;
    if (Math.abs(delta) > 4) dragMovedRef.current = true;

    const singleSetWidth = track.scrollWidth / 3;
    let targetPos = dragStartScrollRef.current - delta;

    // Buffer range wrapping: prevents dragging to the absolute DOM end
    if (singleSetWidth > 0) {
      while (targetPos >= singleSetWidth * 2) {
        targetPos -= singleSetWidth;
        dragStartScrollRef.current -= singleSetWidth;
      }
      while (targetPos < singleSetWidth * 0.2) {
        targetPos += singleSetWidth;
        dragStartScrollRef.current += singleSetWidth;
      }
    }

    track.scrollLeft = targetPos;
    currentScrollFloatRef.current = targetPos;
  }, []);

  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 800);
  }, []);

  // Touch handling — mirrors the mouse drag pause/resume behavior, but
  // deliberately does NOT set scrollLeft manually. touch-action: pan-x
  // (already set in FeaturedWork.scss) tells the browser to handle the
  // actual horizontal scroll motion natively, which is what makes touch
  // scrolling feel correct on iOS/Android. Without this, the rAF loop
  // above kept overwriting scrollLeft every frame while a finger was
  // dragging, since isPaused never became true from a touch gesture,
  // that's what was fighting the user's swipe and making it feel stuck.
  const onTouchStart = useCallback((e) => {
    const track = trackRef.current;
    if (!track) return;
    isDraggingRef.current = true;
    dragMovedRef.current = false;
    dragStartXRef.current = e.touches[0].clientX;
    setIsPaused(true);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const delta = e.touches[0].clientX - dragStartXRef.current;
    if (Math.abs(delta) > 6) dragMovedRef.current = true;
    // No manual scrollLeft assignment here — native touch scrolling
    // handles the actual movement. currentScrollFloatRef stays in sync
    // via the rAF loop's paused branch above (`currentScrollFloatRef.current = track.scrollLeft`).
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 800);
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, [onPointerMove, endDrag, onTouchMove, onTouchEnd]);

  const handleCardActivate = (project) => {
    if (dragMovedRef.current) return;
    setActiveProject(project);
    setIsPaused(true);
  };

  const closeModal = useCallback(() => {
    setActiveProject(null);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 300);
  }, []);

  useEffect(() => {
    if (!activeProject) return;
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeProject, closeModal]);

  // Render Bento Set
  const renderBentoSet = (keyPrefix, isFirstSet = false) => (
    <React.Fragment key={keyPrefix}>
      {/* Column 1: Slot 08 + 09 */}
      <div className="fw__bento-col">
        <WorkCard 
          project={PROJECTS.p08} 
          sequenceNum={8}
          forceRevealed={isFirstSet && !landingDone ? false : undefined}
          onClick={() => handleCardActivate(PROJECTS.p08)} 
        />
        <WorkCard 
          project={PROJECTS.p09} 
          sequenceNum={9}
          forceRevealed={isFirstSet && !landingDone ? false : undefined}
          onClick={() => handleCardActivate(PROJECTS.p09)} 
        />
      </div>

      {/* Column 2: Slot 10 */}
      <div className="fw__bento-col">
        <WorkCard 
          project={PROJECTS.p10} 
          sequenceNum={10}
          forceRevealed={isFirstSet && !landingDone ? false : undefined}
          onClick={() => handleCardActivate(PROJECTS.p10)} 
        />
      </div>

      {/* Column 3: Slot 01 */}
      <div className="fw__bento-col">
        <WorkCard 
          project={PROJECTS.p01} 
          sequenceNum={1}
          innerRef={isFirstSet ? card01Ref : undefined}
          forceRevealed={isFirstSet && !landingDone ? revealedSeq[1] : undefined}
          onClick={() => handleCardActivate(PROJECTS.p01)} 
        />
      </div>

      {/* Block 4: Slot 02 (Wide) over Slots 03 & 04 */}
      <div className="fw__bento-group">
        <WorkCard 
          project={PROJECTS.p02} 
          sequenceNum={2}
          forceRevealed={isFirstSet && !landingDone ? revealedSeq[2] : undefined}
          onClick={() => handleCardActivate(PROJECTS.p02)} 
        />
        <div className="fw__bento-row">
          <WorkCard 
            project={PROJECTS.p03} 
            sequenceNum={3}
            forceRevealed={isFirstSet && !landingDone ? revealedSeq[3] : undefined}
            onClick={() => handleCardActivate(PROJECTS.p03)} 
          />
          <WorkCard 
            project={PROJECTS.p04} 
            sequenceNum={4}
            forceRevealed={isFirstSet && !landingDone ? revealedSeq[4] : undefined}
            onClick={() => handleCardActivate(PROJECTS.p04)} 
          />
        </div>
      </div>

      {/* Block 5: Slots 05 & 06 over Slot 07 */}
      <div className="fw__bento-group">
        <div className="fw__bento-row">
          <WorkCard 
            project={PROJECTS.p05} 
            sequenceNum={5}
            forceRevealed={isFirstSet && !landingDone ? revealedSeq[5] : undefined}
            onClick={() => handleCardActivate(PROJECTS.p05)} 
          />
          <WorkCard 
            project={PROJECTS.p06} 
            sequenceNum={6}
            forceRevealed={isFirstSet && !landingDone ? revealedSeq[6] : undefined}
            onClick={() => handleCardActivate(PROJECTS.p06)} 
          />
        </div>
        <WorkCard 
          project={PROJECTS.p07} 
          sequenceNum={7}
          forceRevealed={isFirstSet && !landingDone ? revealedSeq[7] : undefined}
          onClick={() => handleCardActivate(PROJECTS.p07)} 
        />
      </div>
    </React.Fragment>
  );

  return (
    <section className="fw" ref={sectionRef} aria-label="Featured work">
      <div className="fw__inner">
        <p className="fw__eyebrow">Proof beats promises.</p>
        <h2 className="fw__headline">Featured work</h2>
      </div>

      <div
        className="fw__track"
        ref={trackRef}
        onMouseEnter={() => landingDone && setIsPaused(true)}
        onMouseLeave={() => { if (landingDone && !isDraggingRef.current) setIsPaused(false); }}
        onMouseDown={onPointerDown}
        onTouchStart={onTouchStart}
        role="list"
      >
        {renderBentoSet("set1", true)}
        {renderBentoSet("set2", false)}
        {renderBentoSet("set3", false)}
      </div>

      {/* Full-Image Modal */}
      {activeProject && (
        <div className="fw-modal" onClick={closeModal}>
          <div
            className="fw-modal__panel"
            style={{ backgroundImage: `url(${activeProject.image})` }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={activeProject.title}
          >
            <button
              type="button"
              className="fw-modal__close"
              onClick={closeModal}
              aria-label="Close"
            >
              &times;
            </button>
            
            <div className="fw-modal__gradient" />

            <div className="fw-modal__content">
              <span className="fw-modal__tag">
                {activeProject.category} | {activeProject.deliverable}
              </span>
              <p className="fw-modal__description">{activeProject.description}</p>
              <p className="fw-modal__location">{activeProject.location}</p>
            </div>

            {/* Custom Add-On badge — hidden for now */}
            {/* {!activeProject.hideAddon && (
              <div className="fw-modal__addon-badge">
                Custom Add-On
              </div>
            )} */}
          </div>
        </div>
      )}
    </section>
  );
}